/* eslint-disable @typescript-eslint/no-explicit-any */
import { ColumnDef } from '@tanstack/react-table';
import { useQuery } from 'react-query';
import { useMemo, useState, useRef, useEffect } from 'react';
import { Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';
import FeatherIcon from 'feather-icons-react';

import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import AdvanceTable from 'components/base/AdvanceTable';
import productSearchService from 'service/productSearch';
import AdvanceTableCPagination from 'components/base/AdvanceTableCPagination';
import { pageToOffset } from 'utils/pageToOffset';
import unitService from 'service/units';
import Button from 'components/base/Button';
import { showAlert } from 'store/alert/alert.thunk';
import { Spinner } from 'react-bootstrap';
import inventoryItemService from 'service/inventoryItem';
import './styles.scss';

export type SearchDataType = {
  product_name: string;
  variation: string;
  barcode: string;
  units: string;
  gupid: number;
  amount: number;
  total_quantity: number;
  price_whosale: any;
  quantity_asked: any;
  measure_unit_kind_pid: number;
  package_quantity: any;
  isChecked?: any;
};

interface NotInListsProps {
  setOpenNotInList?: any;
  refetchInventoryItem?: any;
}

const NotInLists = ({
  setOpenNotInList,
  refetchInventoryItem
}: NotInListsProps) => {
  const { t, i18n } = useTranslation();
  const { state } = useLocation();
  const { warehouse_pid, inventory_pid } = state;
  const dispatch: Dispatch<any> = useDispatch();
  const inputRef = useRef<HTMLInputElement | any>();

  const [loading, setLoading] = useState(false);
  const [pageCount, setPageCount] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchValue, setSearchValue] = useState('');
  const [measureunitOption, setMeasureUnitOption] = useState<any>([]);
  const [selectedMeasureUnitId, setSelectedMeasureUnitId] = useState<any>(null);
  const [negativeValue, setNegativeValue] = useState(false);
  const [positiveValue, setPositiveValue] = useState(false);
  const [selectedData, setSelectedData] = useState<any>([]);
  const [selectedAll, setSelectedAll] = useState(false);

  //GET UNITS to options
  useQuery(['GET_UNITS'], async () => {
    await unitService
      .getList({ offset: 0, limit: 100, status: true })
      .then((res: any) => {
        const options = res?.measure_units?.map((option: any) => ({
          value: option?.pid || 0,
          label: option?.name?.[i18n?.language]
        }));
        setMeasureUnitOption(options);
      });
  });

  const { data, isFetching, isLoading } = useQuery(
    [
      'GET_PRODUCT',
      currentPage,
      searchValue,
      warehouse_pid,
      inventory_pid,
      selectedMeasureUnitId,
      negativeValue,
      positiveValue
    ],
    () => {
      if (warehouse_pid && inventory_pid) {
        const res = productSearchService
          .getListProduct({
            offset: pageToOffset(currentPage, 10),
            limit: 10,
            search: searchValue,
            warehouse_pid: warehouse_pid,
            not_inventory_pid: inventory_pid,
            is_positive: positiveValue ? true : undefined,
            is_negative: negativeValue ? true : undefined,
            measure_unit_pid: selectedMeasureUnitId
          })
          .then((res: any) => {
            setPageCount(Math.ceil(res?.count / 10));
            return res;
          });
        return res;
      }
    },
    {
      enabled:
        !!inventory_pid ||
        !!warehouse_pid ||
        !!currentPage ||
        !searchValue ||
        searchValue?.length > 2 ||
        !!selectedMeasureUnitId ||
        !!positiveValue ||
        !!negativeValue
    }
  );

  const totalCount = data?.count || 0;
  const loadingData = isLoading || isFetching;

  const generateData: SearchDataType[] = useMemo(() => {
    return (
      data?.products?.map((items: any) => {
        const isSelected = selectedData.some(
          (selectedItem: any) => selectedItem.barcode === items?.barcode
        );
        return {
          name: items?.name,
          variation: items?.nomenclature?.is_variation ? t('yes') : t('no'),
          barcode: items?.barcode,
          original_barcode: items?.nomenclature?.barcode_original,
          unit: items?.nomenclature?.measure_unit?.name?.[i18n?.language],
          quantity: items?.total_quantity || 0,
          nomenclature_id: items?.nomenclature_id,
          nomenclature_pid: items?.nomenclature_pid || 0,
          currency_id: items?.currency_id,
          currency_pid: items?.currency_pid || 0,
          total_quantity: items?.total_quantity,
          price_in: items?.price_in ?? 0,
          guid: items?.id,
          gupid: items?.pid,
          isChecked: isSelected
        };
      }) ?? []
    );
  }, [data, t, i18n?.language, selectedData]);

  const handleRowCheckbox = (product: SearchDataType) => {
    const barcode = product.barcode;
    const isCurrentlySelected = selectedData.some(
      (item: any) => item.barcode === barcode
    );

    if (isCurrentlySelected) {
      // Remove from selectedData
      setSelectedData((prev: any) =>
        prev.filter((item: any) => item.barcode !== barcode)
      );
    } else {
      // Add to selectedData
      const productToAdd = {
        ...product,
        isChecked: true
      };
      setSelectedData((prev: any) => [...prev, productToAdd]);
    }
  };

  const searchTableColumns: ColumnDef<SearchDataType>[] = [
    {
      id: 'action',
      header: () => (
        <div className="d-flex align-items-center justify-content-center">
          <Form.Check
            checked={selectedAll}
            onChange={e => setSelectedAll(e.target.checked)}
            className="cursor-pointer large-checkbox"
          />
        </div>
      ),
      cell: ({ row }) => {
        const product = row.original;
        const isChecked = selectedData.some(
          (item: any) => item.barcode === product.barcode
        );

        return (
          <div>
            {!selectedAll && (
              <Form.Check
                checked={isChecked}
                onChange={() => handleRowCheckbox(product)}
                className="cursor-pointer large-checkbox"
              />
            )}
          </div>
        );
      },
      meta: {
        cellProps: { className: 'text-900 p-2' }
      }
    },
    {
      accessorKey: 'order_number',
      enableSorting: false,
      header: '№',
      cell: rowData => {
        const orderNumber = rowData.row.index + 1 + (currentPage - 1) * 10;
        return <div>{orderNumber}</div>;
      },
      meta: {
        headerProps: { style: { width: '5%' } },
        cellProps: { className: 'text-900 p-2' }
      }
    },
    {
      accessorKey: 'name',
      header: t('product_names'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'barcode',
      header: t('barcode'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'unit',
      header: t('measure_unit'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'price_in',
      header: t('price_admission'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'total_quantity',
      header: t('quantity'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    }
  ];

  const table = useAdvanceTable({
    data: generateData,
    columns: searchTableColumns,
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: false
  });

  const handleAdd = () => {
    if (selectedAll) {
      setLoading(true);
      const createdDataByPattern = {
        inventory_pid: inventory_pid,
        pattern: '',
        search: searchValue,
        is_positive: positiveValue ? true : undefined,
        is_negative: negativeValue ? true : undefined,
        measure_unit_pid: selectedMeasureUnitId
      };
      inventoryItemService
        .createInventoryItemByPattern(createdDataByPattern)
        .then((res: any) => {
          if (res) {
            dispatch(
              showAlert({
                title: t('successfully_added'),
                type: 'success'
              })
            );
          }
          setLoading(false);
          setOpenNotInList(false);
          refetchInventoryItem();
        });
    } else {
      const createdData = selectedData?.map((item: any) => {
        return {
          quantity_actual: item?.quantity ?? 0,
          quantity: item?.quantity ?? 0,
          nomenclature_id: item?.nomenclature_id,
          nomenclature_pid: item?.nomenclature_pid,
          currency_pid: item.currency_pid,
          price_in: item.price_in ?? 0,
          product_id: item.guid,
          product_pid: item.gupid,
          status_id: 'ba94f8a8-3a42-4d1e-a351-0e5349e3f512',
          status_pid: 5,
          write_off: true
        };
      });
      /* prettier-ignore */
      if (createdData) {
        setLoading(true);
            inventoryItemService
              .createInventoryItem({
                inventory_pid: inventory_pid,
                inventory_items: createdData
              })
              .then((res: any) => {
                if (res) {
                  dispatch(
                    showAlert({
                      title: t('successfully_added'),
                      type: 'success'
                    })
                  );
                }
               setLoading(false);
               setOpenNotInList(false);
               refetchInventoryItem();
              });
          }
    }
  };

  const handleLikeThero = () => {
    if (selectedAll) {
      setLoading(true);
      const createdDataByPattern = {
        inventory_pid: inventory_pid,
        pattern: 'WRITEOFF',
        search: searchValue,
        is_positive: positiveValue ? true : undefined,
        is_negative: negativeValue ? true : undefined,
        measure_unit_pid: selectedMeasureUnitId
      };
      inventoryItemService
        .createInventoryItemByPattern(createdDataByPattern)
        .then((res: any) => {
          if (res) {
            dispatch(
              showAlert({
                title: t('product_successfully_cancelled'),
                type: 'success'
              })
            );
          }
          setLoading(false);
          setOpenNotInList(false);
          refetchInventoryItem();
        });
    } else {
      const createdData = selectedData?.map((item: any) => {
        return {
          quantity_actual: 0,
          quantity: item?.quantity ?? 0,
          nomenclature_id: item?.nomenclature_id,
          nomenclature_pid: item?.nomenclature_pid,
          currency_pid: item.currency_pid,
          price_in: item.price_in ?? 0,
          product_id: item.guid,
          product_pid: item.gupid,
          status_id: 'ba94f8a8-3a42-4d1e-a351-0e5349e3f512',
          status_pid: 5,
          write_off: true
        };
      });
      /* prettier-ignore */
      if (createdData) {
        setLoading(true);
            inventoryItemService
              .createInventoryItem({
                inventory_pid: inventory_pid,
                inventory_items: createdData
              })
              .then((res: any) => {
                if (res) {
                  dispatch(
                    showAlert({
                      title: t('product_successfully_cancelled'),
                      type: 'success'
                    })
                  );
                }
               setLoading(false);
               setOpenNotInList(false);
               refetchInventoryItem();
              });
          }
    }
  };

  useEffect(() => {
    if (inputRef.current) {
      setTimeout(() => {
        inputRef.current.focus();
      }, 500);
    }
  }, []);

  return (
    <div>
      <div className="d-flex gap-2 mb-3 align-items-center">
        <div className="form-icon-container w-30">
          <Form.Floating>
            <Form.Control
              ref={inputRef}
              type="text"
              placeholder={t('enter_product')}
              onFocus={e => e.target.select()}
              onChange={e => setSearchValue(e?.target?.value)}
              className="form-icon-input"
            />
            <label
              htmlFor="floatingInputCustom"
              className="form-icon-label text-700"
            >
              {t('enter_product')}
            </label>
          </Form.Floating>
          <FontAwesomeIcon
            icon={faSearch}
            className="text-900 fs-9 form-icon"
          />
        </div>
        <div className="react-select-container">
          <Form.Floating>
            <Form.Select
              value={selectedMeasureUnitId}
              onChange={(e: any) => {
                const selectedOption = measureunitOption.find(
                  (option: any) =>
                    Number(option.value) === Number(e.target.value)
                );

                if (selectedOption) {
                  setSelectedMeasureUnitId(selectedOption?.value);
                }
              }}
            >
              <option className="d-none" value=""></option>
              {measureunitOption?.map((option: any) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>

            <label htmlFor="floatingInputCustom">{t('measure_unit')}</label>
            {selectedMeasureUnitId && (
              <Button
                type="button"
                variant="link"
                style={{
                  position: 'absolute',
                  right: '5%',
                  top: '15%',
                  cursor: 'pointer'
                }}
                onClick={() => {
                  setSelectedMeasureUnitId('');
                }}
              >
                <FeatherIcon icon="delete" size={16} />
              </Button>
            )}
          </Form.Floating>
        </div>

        <Form.Group className="d-flex gap-2 align-items-center">
          <Form.Check
            type="radio"
            checked={positiveValue}
            onChange={e => {
              setPositiveValue(e.target.checked);
              if (e.target.checked) {
                setNegativeValue(false); // Deselect negativeValue
              }
            }}
            label={t('positive_products')}
            className="cursor-pointer"
          />
          <Form.Check
            type="radio"
            checked={negativeValue}
            onChange={e => {
              setNegativeValue(e.target.checked);
              if (e.target.checked) {
                setPositiveValue(false); // Deselect positiveValue
              }
            }}
            label={t('minus_products')}
            className="cursor-pointer"
          />
        </Form.Group>
      </div>

      <AdvanceTableProvider {...table}>
        <div className="p-3 mb-2 d-flex justify-content-between align-items-center border-top border-bottom">
          {selectedAll && (
            <p className="mb-0 fw-semi-bold text-900">
              Выбраны все {totalCount} элементов, соответствующие этому поиску
            </p>
          )}
          <div className="d-flex justify-content-between gap-2 ms-auto">
            <Button
              disabled={generateData?.length === 0}
              variant="primary"
              type="button"
              loading={loading}
              onClick={() => handleAdd()}
              loadingPosition="start"
            >
              {t('add_as_is')}
            </Button>
            <Button
              disabled={generateData?.length === 0}
              type="button"
              variant="primary"
              loading={loading}
              onClick={() => handleLikeThero()}
              loadingPosition="start"
            >
              {t('add_by_cancellation')}
            </Button>
          </div>
        </div>

        <div>
          <AdvanceTable
            tableProps={{ className: 'phoenix-table fs-9' }}
            isLoading={loadingData}
          />

          {loading && (
            <div className="loading-table">
              <span>
                <Spinner
                  animation="border"
                  role="status"
                  style={{
                    color: '#8200BF',
                    width: '3rem',
                    height: '3rem'
                  }}
                ></Spinner>
              </span>
            </div>
          )}
        </div>
        <AdvanceTableCPagination
          count={pageCount}
          page={currentPage}
          setCurrentPage={setCurrentPage}
        />
      </AdvanceTableProvider>
    </div>
  );
};

export default NotInLists;
